/**
 * MUITable
 *
 * A flexible, scrollable table component with optional columns for chat, info, and actions.
 * It supports column-level filtering (status, department), truncated-text tooltips,
 * a contextual actions menu per row, and an optional chat popover per row.
 *
 * Key behaviors
 * - Uses OverlayScrollbars for consistent cross-platform scroll styling.
 * - TextOverflow subcomponent only shows tooltip when content is actually truncated.
 * - Filters are multi-select; parent is notified via onFilterChange with normalized values.
 * - Row clicks are suppressed when interacting with menus or popovers.
 *
 * Props (see MuiTableProps)
 * - columnNames: TableHeader[]
 * - rowsData: any[]
 * - showChatColumn, showInfoColumn, showActionsColumn, showActionsColumnWithOptions
 * - onRowClick, onOpenChat, onCommentSubmit, commentsByRow, activeChatRow
 * - onFilterChange, isRequest, departmentsList, canWrite
 */
// src/components/MUITableTable/MUITableTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./mui-table.scss";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MuiTableProps } from "./mui-table-props";
import TableHeaderArrow from "../../../assets/icons/sortIcon.svg";
import ChatIcon from "../../../assets/icons/chatIcon.svg";
import ActionsIcon from "../../../assets/icons/actionsIcon.svg";
import FilterIcon from "../../../assets/icons/filter-White.svg";
import NoRecordsComponent from "../no-records-component/no-records-component";
import ChatPopover from "../chat-popover/chat-popover";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { ClickAwayListener, Popover, Tooltip } from "@mui/material";
import infoIcon from "../../../assets/icons/info-icon.svg";

export interface TableHeader {
  key: string;
  label: string;
  filterable?: boolean;
}
interface FilterOption {
  key: number | string;
  value: string;
}

interface Filters {
  [columnKey: string]: FilterOption[];
}

// 👇 Renders cell text with a title only when it's visually truncated
const TextOverflow: React.FC<{ value: string, onClickRow?: () => void }> = ({ value, onClickRow }) => {
  const spanRef = useRef<any>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = () => {
    const el = spanRef.current;
    if (!el) return;
    const overflowed = el.scrollWidth > el.clientWidth + 1;
    setIsOverflow(overflowed);
  };

  useEffect(() => {
    checkOverflow();
    const onResize = () => checkOverflow();
    globalThis.addEventListener("resize", onResize);
    return () => globalThis.removeEventListener("resize", onResize);
  }, [value]);

  return (
    <Tooltip
      title={value}
      placement="bottom-start"
      disableHoverListener={!isOverflow}
    >
      <button
        ref={spanRef}
        className="cell-text-ellipsis"
        onMouseEnter={checkOverflow}
        onClick={() => { if(onClickRow) {onClickRow();} }}
        style={{ background: "none", border: "none", padding: "0px 16px", margin: 0, textAlign: "left", width: "100%", cursor: onClickRow ? "pointer" : "default" }} 
      >
        {value}
      </button>
    </Tooltip>
  );
};

// 👇 Define sortable column keys here - if a column key matches, it will show sort icon
const SORTABLE_COLUMNS = new Set([
  "milestone",
  "title", // lowercase title
  "status",
  "startDate",
  "endDate",
  "progress",
  "percentageComplete", // alternative naming
  "assignedTo",
  "initiativeTitle",
  "objectives",
  "date",
  "timeline",
  "department",
]);

// 👇 Define center-aligned column keys here

const MUITable: React.FC<MuiTableProps> = ({
  columnNames,
  rowsData = [],
  showChatColumn,
  showActionsColumn,
  showActionsColumnWithOptions,
  onRowClick,
  menuOptions = [],
  selectedMenuOption,
  setSelectedMenuOption,
  isRequest = false,
  onFilterChange,
  onOpenChat,
  commentsByRow,
  activeChatRow,
  onCommentSubmit,
  departmentsList = [], // Add departmentsList prop with default empty array
  canWrite,
  showInfoColumn,
  isFromMilestone,
  fromInitiativeTable = false,
  noRecordsTitle,
  noRecordsMessage,
  renderExpandedRow,
}) => {// NOSONAR
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const filterIconRefs = useRef<any>({});
  const [chatAnchorEl, setChatAnchorEl] = useState<HTMLElement | null>(null);
  const [blockRowClick, setBlockRowClick] = useState(false);

  // 👇 Sort menu state
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [sortMenuColumn, setSortMenuColumn] = useState<string>("");

  // Open sort menu
  const handleOpenSortMenu = (
    event: React.MouseEvent<HTMLElement>,
    columnKey: string,
  ) => {
    event.stopPropagation();
    const target = event.currentTarget;

    if (openFilter) {
      setOpenFilter(null);
      setTimeout(() => {
        setSortMenuAnchor(target);
        setSortMenuColumn(columnKey);
      }, 0);
      return;
    }

    setSortMenuAnchor(target);
    setSortMenuColumn(columnKey);
  };

  // Close sort menu
  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
    setSortMenuColumn("");
    // Block row clicks briefly to prevent accidental row click when menu closes
    setBlockRowClick(true);
    setTimeout(() => setBlockRowClick(false), 100);
  };

  // Handle sort selection from menu
  const handleSortSelection = (
    direction: "asc" | "desc" | null,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }

    // Close menu first, then update state to prevent drawer issues
    handleCloseSortMenu();

    // Use setTimeout to defer state update
    setTimeout(() => {
      if (direction === null) {
        // Clear sorting
        setSortConfig(null);
      } else {
        setSortConfig({ key: sortMenuColumn, direction });
      }
    }, 100);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedMenuOption(row?.id);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuOption(null);
    setSelectedRow(null);
  };

  const openMenu = Boolean(anchorEl);

  const ITEM_HEIGHT = 48;
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: FilterOption[];
  }>(() => ({
    ...(isRequest ? { status: [{ key: 2, value: "Pending" }] } : {}),
    department: [], // ✅ initialize department filter as empty array
  }));

  // Close sort menu on outside click and Escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const element = target as Element;

      // Close sort menu if clicking outside (but not on MUI Popover or sort icon)
      if (sortMenuAnchor) {
        const isClickInsidePopover = element.closest(
          ".MuiPopover-root, .MuiPopover-paper",
        );
        const isClickOnSortIcon =
          element.closest('[alt="Sort"]') ||
          element.getAttribute("alt") === "Sort";
        const isClickInSortMenu = element.closest('[role="presentation"]');

        if (!isClickInsidePopover && !isClickOnSortIcon && !isClickInSortMenu) {
          handleCloseSortMenu();
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && sortMenuAnchor) {
        handleCloseSortMenu();
      }
    };

    if (sortMenuAnchor) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sortMenuAnchor]);

  // Ensure parent knows about default filter on initial render (e.g., Pending for requests)
  useEffect(() => {
    if (!onFilterChange) return;
    if (isRequest) {
      onFilterChange({ status: ["pending"] });
    }
  }, []);

  const handleFilterSelect = (columnKey: string, option: FilterOption) => {
    setSelectedFilters((prev) => {
      const alreadySelected = prev[columnKey]?.some(
        (f) => f.key === option.key,
      );

      const updated = {
        ...prev,
        [columnKey]: alreadySelected
          ? prev[columnKey].filter((f) => f.key !== option.key)
          : [...(prev[columnKey] || []), option],
      };

      // ✅ Notify parent about filter change
      if (onFilterChange) {
        if (columnKey === "status") {
          const statuses = (updated.status || []).map((f) =>
            f.value.toLowerCase(),
          );
          // When nothing selected, send undefined so parent can show all
          onFilterChange({ status: statuses.length ? statuses : undefined });
        }
        if (columnKey === "department") {
          const departments = updated.department.map((f) => f.value);
          onFilterChange({ department: departments });
        }
      }

      return updated;
    });
  };

  const filters: Filters = {
    status: [
      { key: 1, value: "Approved" },
      { key: 2, value: "Pending" },
      { key: 3, value: "Rejected" },
    ],
    department: departmentsList,
  };
  const filteredRows = isRequest
    ? rowsData
    : rowsData.filter((row) => {
        return Object.entries(selectedFilters).every(([key, selected]) => {
          if (!selected || selected.length === 0) return true;

          const selectedValues = new Set(selected.map((f) => f.value));

          if (key === "status") {
            const cell = row[key] as
              | React.ReactElement<{ status?: string }>
              | string;
            if (React.isValidElement(cell)) {
              return selectedValues.has(cell.props.status ?? "");
            }
            return selectedValues.has(cell);
          }

          if (key === "department") {
            const cell = row[key];
            if (React.isValidElement(cell)) {
              const props = cell.props as any;
              const deptValue =
                props.departmentName || props.department || props.name;
              const matches = selectedValues.has(deptValue);
              return matches;
            } else {
              const matches = selectedValues.has(cell);
              return matches;
            }
          }

          // Default case for plain fields
          return selectedValues.has(row[key]);
        });
      });
  // ✅ Sorting state - 3-state cycle: asc → desc → reset
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  // ✅ compute sorted data after filtering
  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;
    const metadataKey = "__sortValues";
    const { key, direction } = sortConfig;
    return [...filteredRows].sort((a, b) => {// NOSONAR
      const aMetadata =
        a && typeof a === "object" && metadataKey in a
          ? (a as Record<string, any>)[metadataKey]?.[key]
          : undefined;
      const bMetadata =
        b && typeof b === "object" && metadataKey in b
          ? (b as Record<string, any>)[metadataKey]?.[key]
          : undefined;
               
      let aVal = aMetadata ?? a[key];
      let bVal = bMetadata ?? b[key];

      // Helper function to deeply extract comparable text from React/JSX structures
      const extractValue = (element: any): string | number | null => {// NOSONAR
        if (element == null) {
          return null;
        }

        if (typeof element === "number") {
          return element;
        }

        if (typeof element === "string") {
          const trimmed = element.trim();
          return trimmed === "" ? null : trimmed;
        }

        if (Array.isArray(element)) {
          const flattened = element
            .map((item) => // NOSONAR
              extractValue(item))
            .filter((val): val is string | number =>// NOSONAR
              val !== null && val !== "");

          if (flattened.length === 0) {
            return null;
          }

          const stringValues = flattened.filter(
            (val): val is string =>// NOSONAR
            typeof val === "string",
          );
          const trimmedStrings = stringValues
            .map((value) =>// NOSONAR 
              value.trim())
            .filter((value) =>// NOSONAR 
              value !== "");
          if (trimmedStrings.length > 0) {
            return trimmedStrings.join(", ");
          }

          const numericValue = flattened.find(
            (val): val is number => // NOSONAR 
            typeof val === "number" && !Number.isNaN(val), 
          );
          if (numericValue !== undefined) {
            return numericValue;
          }

          return null;
        }

        const propCandidates = [
          "dataValue",
          "dataSortValue",
          "data-sort-value",
          "primary",
          "primaryText",
          "title",
          "label",
          "text",
          "name",
          "departmentName",
          "department",
          "status",
          "value",
          "alt",
        ];

        if (React.isValidElement(element)) {
          const props = (element.props || {}) as Record<string, unknown>;
          const typeInfo: any = element.type;
          const typeName =
            typeof typeInfo === "string"
              ? typeInfo
              : (typeInfo?.muiName ?? typeInfo?.displayName ?? "");
          const isTooltip =
            typeName === "Tooltip" || typeName === "ForwardRef(Tooltip)";

          for (const key of propCandidates) {
            if (isTooltip && key === "title") continue;

            const candidate = props[key];
            if (typeof candidate === "string") {
              const trimmed = candidate.trim();
              if (trimmed !== "") {
                return trimmed;
              }
            }
            if (typeof candidate === "number") {
              return candidate;
            }
          }

          if (Object.hasOwn(props, "children")) {
            return extractValue(props.children);
          }

          return null;
        }

        if (typeof element === "object") {
          for (const key of propCandidates) {
            const candidate = (element as Record<string, unknown>)[key];
            if (typeof candidate === "string") {
              const trimmed = candidate.trim();
              if (trimmed !== "") {
                return trimmed;
              }
            }
            if (typeof candidate === "number") {
              return candidate;
            }
          }
        }

        return null;
      };

      // Extract actual values from React elements
      aVal = extractValue(aVal);
      bVal = extractValue(bVal);

      const numericColumnKeys = [
        "progress",
        "percentageComplete",
        "percentCompleted",
        "percentComplete",
      ];

      const parseNumericValue = (input: any): number | null => {
        if (
          input == null ||
          (typeof input === "string" && input.trim() === "")
        ) {
          return null;
        }
         
        if (typeof input === "number" && !Number.isNaN(input)) {
          return input;
        }

        const str = String(input);
        const regex = /-?\d+(?:\.\d+)?/;
        const match = regex.exec(str);
        if (!match) {
          return null;
        }

        const numeric = Number.parseFloat(match[0]);
        return Number.isNaN(numeric) ? null : numeric;
      };

      if (numericColumnKeys.includes(key)) {
        const aNumeric = parseNumericValue(aVal);
        const bNumeric = parseNumericValue(bVal);

        if (aNumeric == null && bNumeric == null) {
          return 0;
        }
        if (aNumeric == null) {
          return 1;
        }
        if (bNumeric == null) {
          return -1;
        }

        if (aNumeric < bNumeric) {
          return direction === "asc" ? -1 : 1;
        }
        if (aNumeric > bNumeric) {
          return direction === "asc" ? 1 : -1;
        }
        return 0;
      }

      // handle nulls / undefined gracefully
      if (aVal == null || aVal === "") return 1;
      if (bVal == null || bVal === "") return -1;
      
      const parseDateValue = (input: string): number | null => {
        const trimmed = input.trim();

        const direct = Date.parse(trimmed);
        if (!Number.isNaN(direct)) {
          return direct;
        }

        const applyOrder = (
          segments: string[],
          order: Array<"Y" | "M" | "D">,
        ): number | null => {// NOSONAR
          let year = 0;
          let month = 0;
          let day = 1;

          order.forEach((token, idx) => {
            const value = Number.parseInt(segments[idx] ?? "", 10);
            if (token === "Y") {
              year = value < 100 ? 2000 + value : value;
            }
            if (token === "M") {
              month = value - 1;
            }
            if (token === "D") {
              day = value;
            }
          });

          if (
            !Number.isFinite(year) ||
            !Number.isFinite(month) ||
            !Number.isFinite(day)
          ) {
            return null;
          }

          if (month < 0 || month > 11 || day < 1 || day > 31) {
            return null;
          }

          const epoch = Date.UTC(year, month, day);
          if (Number.isNaN(epoch)) {
            return null;
          }

          const utcDate = new Date(epoch);
          const matches =
            utcDate.getUTCFullYear() === year &&
            utcDate.getUTCMonth() === month &&
            utcDate.getUTCDate() === day;

          return matches ? epoch : null;
        };

        const datePatterns: Array<{
          regex: RegExp;
          separator: string;
          orders: Array<Array<"Y" | "M" | "D">>;
        }> = [
          {
            regex: /^\d{4}-\d{2}-\d{2}$/,
            separator: "-",
            orders: [["Y", "M", "D"]],
          },
          {
            regex: /^\d{4}\/\d{2}\/\d{2}$/,
            separator: "/",
            orders: [["Y", "M", "D"]],
          },
          {
            regex: /^\d{2}-\d{2}-\d{4}$/,
            separator: "-",
            orders: [
              ["M", "D", "Y"],
              ["D", "M", "Y"],
            ],
          },
          {
            regex: /^\d{2}\/\d{2}\/\d{4}$/,
            separator: "/",
            orders: [
              ["M", "D", "Y"],
              ["D", "M", "Y"],
            ],
          },
          {
            regex: /^\d{2}-\d{2}-\d{2}$/,
            separator: "-",
            orders: [["M", "D", "Y"]],
          },
          {
            regex: /^\d{2}\/\d{2}\/\d{2}$/,
            separator: "/",
            orders: [["M", "D", "Y"]],
          },
        ];

        for (const pattern of datePatterns) {
          if (pattern.regex.exec(trimmed)) {
            const segments = trimmed.split(pattern.separator);
            for (const order of pattern.orders) {
              const result = applyOrder(segments, order);
              if (result !== null) {
                return result;
              }
            }
          }
        }

        return null;
      };

      const normalizeComparableValue = (
        value: string | number,
      ): number | string => {
        if (typeof value === "number") {
          return value;
        }
    
        const trimmed = value.trim();
        if (trimmed === "") {
          return "";
        }

        const parsedDate = parseDateValue(trimmed);
        if (parsedDate !== null &&( key!=="initiativeTitle" && key!=="title")) {
          return parsedDate;
        }

        const percentRegex = /^-?(?:\d+(?:\.\d+)?|\.\d+)\s*%$/;
        const percentMatch = percentRegex.exec(trimmed);
        if (percentMatch &&( key!=="initiativeTitle" && key!=="title")) {
          const numericPortion = Number.parseFloat(
            percentMatch[0].replace("%", ""),
          );
          if (!Number.isNaN(numericPortion)) {
            return numericPortion;
          }
        }

        const numericRegex = /-?\d+(?:\.\d+)?/;
        const numericMatch = numericRegex.exec(trimmed);
        if (numericMatch &&( key!=="initiativeTitle" && key!=="title")) {
          const numeric = Number.parseFloat(numericMatch[0]);
          if (!Number.isNaN(numeric)) {
            return numeric;
          }
        }

        return trimmed.toLowerCase();
      };

      const aComparable = normalizeComparableValue(aVal as string | number);
      const bComparable = normalizeComparableValue(bVal as string | number);
        
      if (typeof aComparable === "number" && typeof bComparable === "number") {
        if (aComparable < bComparable) return direction === "asc" ? -1 : 1;
        if (aComparable > bComparable) return direction === "asc" ? 1 : -1;
        return 0;
      }

      const aStr = String(aComparable);
      const bStr = String(bComparable);

      if (aStr < bStr) return direction === "asc" ? -1 : 1;
      if (aStr > bStr) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortConfig]);

  const userData = JSON.parse(localStorage.getItem("userDetails") || "{}");

  const viewSortedRows = sortedRows.map((row, idx) => {// NOSONAR
    let isShowOptions = true;
    if (row?.isInitiativePage) {
      const isShowOption = row?.owners?.find(
        (owner: any) => owner?.ownerId === userData?.employeeId,
      );
      isShowOptions =
        isShowOption !== undefined ||
        userData?.roles?.find((role: any) => role?.name === "Admin");
    }
    const rowId = isRequest ? row.titleId : row.initiativeId;
    const isChatOpen = activeChatRow === rowId;
    const expandedContent = renderExpandedRow ? renderExpandedRow(row) : null;
    const isExpanded = !!expandedContent;

    return (
      <React.Fragment key={idx + 1}>
        <tr
          onClick={(e) => {
            const target = e.target as HTMLElement;

            // Prevent row click if sort menu is open or blocked
            if (sortMenuAnchor || blockRowClick) {
              e.stopPropagation();
              return;
            }

            // Check if click is inside any MUI Popover
            if (
              document.querySelector(".MuiPopover-root")?.contains(target) ||
              target.closest(".MuiPopover-root")
            ) {
              e.stopPropagation();
              return;
            }

            if (
              !openMenu &&
              !target.closest(".chat-icon") &&
              !target.closest(".info-icon") &&
              !target.closest("button") &&
              !target.closest(".chat-popover-container")
            ) {
              onRowClick?.(row, `${target?.closest(".MuiAvatarGroup-root") ? 1 : 0}`);
            }
          }}
          style={{
            cursor: row.isFaded || onRowClick ? "pointer" : "default",
            opacity: row.isFaded ? 0.5 : 1,
          }}
          className={`${isExpanded ? "row-expanded" : ""} ${isFromMilestone ? "milestone-row" : ""}`}
        >

        {isFromMilestone && columnNames?.find((header)=> header.key === "milestone") ?
          <td colSpan={4} id="milestone-row"> 
            <TextOverflow value={String(row["milestone"])} />
            <tr>
              {columnNames?.filter((header)=> header.key !== "milestone").map((header) => {
                const cellData = row[header.key];
                const isAvatarCell =
                  typeof cellData === "object" && cellData?.type === "avatar";
                const isReactCell = React.isValidElement(cellData);
                let textOverflowValue = "";

                if (
                  cellData !== null &&
                  cellData !== undefined &&
                  (typeof cellData === "string" || typeof cellData === "number")
                ) {
                  textOverflowValue = String(cellData);
                }

                return (
                  <td key={header.key}>
                    {(header?.key === "initiativeTitle" ||
                      header?.key === "title") &&
                    isExpanded ? (
                      <div className="under-review-badge">
                        <span>Under Review</span>
                        <br />
                      </div>
                    ) : null}
                    {isReactCell && cellData}
                    {!isReactCell && isAvatarCell && (
                      <img src={cellData.url} alt="avatar" className="avatar" />
                    )}
                    {!isReactCell && !isAvatarCell && (
                      <TextOverflow value={textOverflowValue} />
                    )}
                  </td>
                );
              })}
              {showActionsColumnWithOptions && (
                <td>
                  {isShowOptions && !row?.isAchieved  && isRequest && row.rawStatus === "pending" && (
                    <>
                      <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={openMenu ? "long-menu" : undefined}
                        aria-expanded={openMenu ? "true" : undefined}
                        aria-haspopup="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, row);
                        }}
                      >
                        <img src={ActionsIcon} alt="Actions" />
                      </IconButton>

                      <Menu
                        id="long-menu"
                        anchorEl={anchorEl}
                        open={openMenu && selectedMenuOption === row?.id}
                        onClose={handleMenuClose}
                        slotProps={{
                          paper: {
                            style: {
                              maxHeight: ITEM_HEIGHT * 4.5,
                              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                              borderRadius: 16,
                              padding: 0,
                            },
                            sx: {
                              "&.MuiPaper-root": {
                                padding: "0px !important",
                                width: "156px",
                              },
                            },
                          },
                          list: {
                            "aria-labelledby": "long-button",
                          },
                        }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        {menuOptions.map((option: any, index: number) => (
                          <MenuItem
                            key={option.key || option.label || index}
                            onClick={(e) => {
                              e.stopPropagation();
                              option.onClick(handleMenuClose, selectedRow);
                            }}
                            style={option.style}
                            sx={{
                              padding: "8px 16px !important",
                            }}
                          >
                            {option.icon &&
                              (React.isValidElement(option.icon) ? (
                                option.icon
                              ) : (
                                <img
                                  src={option.icon}
                                  alt={
                                    typeof option.label === "string"
                                      ? option.label
                                      : "icon"
                                  }
                                />
                              ))}
                            {option.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                  {isShowOptions && !row?.isAchieved && !isRequest && (
                    <>
                      <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={openMenu ? "long-menu" : undefined}
                        aria-expanded={openMenu ? "true" : undefined}
                        aria-haspopup="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, row);
                        }}
                      >
                        <img src={ActionsIcon} alt="Actions" />
                      </IconButton>

                      <Menu
                        id="long-menu"
                        anchorEl={anchorEl}
                        open={openMenu && selectedMenuOption === row?.id}
                        onClose={handleMenuClose}
                        container={document.body}
                        disablePortal={false}
                        slotProps={{
                          paper: {
                            elevation: 0,
                            style: {
                              maxHeight: ITEM_HEIGHT * 3.5,
                              boxShadow: fromInitiativeTable
                                ? "0px 1px 3px rgba(0, 0, 0, 0.012)!important"
                                : "0px 4px 16px rgba(0, 0, 0, 0.12)",
                              borderRadius: 16,
                              padding: 0,
                            },
                            sx: {
                              "--Paper-root-boxShadow": "none !important",
                              "--Paper-shadow": "none !important",
                              "--Paper-elevation": "0 !important",
                              "&.MuiPaper-root": {
                                padding: "0px !important",
                                width: "156px",
                              },
                            },
                          },
                          list: {
                            "aria-labelledby": "long-button",
                            dense: true,
                          },
                        }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        {menuOptions.map((option: any, index: number) => (
                          <MenuItem
                            key={option.key || option.label || index}
                            dense
                            onClick={(e) => {
                              e.stopPropagation();
                              option.onClick(handleMenuClose, selectedRow);
                            }}
                            style={option.style}
                            sx={{
                              padding: "8px 16px !important",
                            }}
                          >
                            {option.icon &&
                              (React.isValidElement(option.icon) ? (
                                option.icon
                              ) : (
                                <img
                                  src={option.icon}
                                  alt={
                                    typeof option.label === "string"
                                      ? option.label
                                      : "icon"
                                  }
                                />
                              ))}
                            {option.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                  {row?.isAchieved && (
                      <IconButton
                        aria-label="more"
                        id="long-button"
                        sx={{
                          cursor: "default",
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />)}
                </td>
              )}
            </tr>
          </td>
        :<>
            {columnNames?.filter((header)=> header.key !== "milestone").map((header) => {
              const cellData = row[header.key];
              const isAvatarCell =
                typeof cellData === "object" && cellData?.type === "avatar";
              const isReactCell = React.isValidElement(cellData);
              let textOverflowValue = "";

              if (
                cellData !== null &&
                cellData !== undefined &&
                (typeof cellData === "string" || typeof cellData === "number")
              ) {
                textOverflowValue = String(cellData);
              }

              return (
                <td key={header.key}>
                  {(header?.key === "initiativeTitle" ||
                    header?.key === "title") &&
                  isExpanded ? (
                    <div className="under-review-badge">
                      <span>Under Review</span>
                      <br />
                    </div>
                  ) : null}
                  {isReactCell && cellData}
                  {!isReactCell && isAvatarCell && (
                    <img src={cellData.url} alt="avatar" className="avatar" />
                  )}
                  {!isReactCell && !isAvatarCell && (
                    <TextOverflow value={textOverflowValue} onClickRow={() => {
                        onRowClick?.(row, header?.key);
                    }} />
                  )}
                </td>
              );
            })}
            {showChatColumn && (
              <td>
                {isShowOptions ? (
                  <>
                    <button
                      style={{ background: "none", border: "none", padding: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeChatRow === rowId) {
                          onOpenChat?.(null);
                          setChatAnchorEl(null);
                        } else {
                          setChatAnchorEl(e.currentTarget);
                          onOpenChat?.(rowId);
                        }
                      }}
                    >
                      <img
                        className="chat-icon"
                        src={ChatIcon}
                        alt="Chat"
                        style={{ cursor: "pointer" }}
                      />
                    </button>
                    <Popover
                      open={isChatOpen && !!chatAnchorEl}
                      anchorEl={chatAnchorEl}
                      onClose={() => {
                        onOpenChat?.(null);
                        setChatAnchorEl(null);
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      slotProps={{
                        paper: {
                          style: {
                            width: 434,
                            borderRadius: 16,
                            overflow: "visible",
                            marginLeft: 16,
                            marginTop: 14,
                          },
                        },
                      }}
                    >
                      <div
                        className="chat-popover-container"
                      >
                        <ChatPopover
                          comments={commentsByRow?.[rowId] || []}
                          onCommentSubmit={(html, parentId, attachments) =>
                            onCommentSubmit?.(rowId, html, parentId, attachments)
                          }
                          onClose={() => onOpenChat?.(null)}
                          canWrite={canWrite}
                        />
                      </div>
                    </Popover>
                  </>
                ) : (
                  ""
                )}
              </td>
            )}
            {showInfoColumn && (
              <td>
                <Tooltip title={row?.message} arrow placement="top">
                  <img
                    className="info-icon"
                    src={infoIcon}
                    alt="Info"
                    style={{
                      marginTop: "9px",
                      cursor: "pointer",
                      width: "23px",
                      height: "23px",
                    }}
                  />
                </Tooltip>
              </td>
            )}
            {showActionsColumn && (
              <td>
                <img className="actions-icon" src={ActionsIcon} alt="Actions" />
              </td>
            )}
            {showActionsColumnWithOptions && (
              <td>
                {isShowOptions && isRequest && row.rawStatus === "pending" && (
                  <>
                    <IconButton
                      aria-label="more"
                      id="long-button"
                      aria-controls={openMenu ? "long-menu" : undefined}
                      aria-expanded={openMenu ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, row);
                      }}
                    >
                      <img src={ActionsIcon} alt="Actions" />
                    </IconButton>

                    <Menu
                      id="long-menu"
                      anchorEl={anchorEl}
                      open={openMenu && selectedMenuOption === row?.id}
                      onClose={handleMenuClose}
                      slotProps={{
                        paper: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                            borderRadius: 16,
                            padding: 0,
                          },
                          sx: {
                            "&.MuiPaper-root": {
                              padding: "0px !important",
                              width: "156px",
                            },
                          },
                        },
                        list: {
                          "aria-labelledby": "long-button",
                        },
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {menuOptions.map((option: any, index: number) => (
                        <MenuItem
                          key={option.key || option.label || index}
                          onClick={(e) => {
                            e.stopPropagation();
                            option.onClick(handleMenuClose, selectedRow);
                          }}
                          style={option.style}
                          sx={{
                            padding: "8px 16px !important",
                          }}
                        >
                          {option.icon &&
                            (React.isValidElement(option.icon) ? (
                              option.icon
                            ) : (
                              <img
                                src={option.icon}
                                alt={
                                  typeof option.label === "string"
                                    ? option.label
                                    : "icon"
                                }
                              />
                            ))}
                          {option.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                {isShowOptions && !isRequest && (
                  <>
                    <IconButton
                      aria-label="more"
                      id="long-button"
                      aria-controls={openMenu ? "long-menu" : undefined}
                      aria-expanded={openMenu ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, row);
                      }}
                    >
                      <img src={ActionsIcon} alt="Actions" />
                    </IconButton>

                    <Menu
                      id="long-menu"
                      anchorEl={anchorEl}
                      open={openMenu && selectedMenuOption === row?.id}
                      onClose={handleMenuClose}
                      container={document.body}
                      disablePortal={false}
                      slotProps={{
                        paper: {
                          elevation: 0,
                          style: {
                            maxHeight: ITEM_HEIGHT * 3.5,
                            boxShadow: fromInitiativeTable
                              ? "0px 1px 3px rgba(0, 0, 0, 0.012)!important"
                              : "0px 4px 16px rgba(0, 0, 0, 0.12)",
                            borderRadius: 16,
                            padding: 0,
                          },
                          sx: {
                            "--Paper-root-boxShadow": "none !important",
                            "--Paper-shadow": "none !important",
                            "--Paper-elevation": "0 !important",
                            "&.MuiPaper-root": {
                              padding: "0px !important",
                              width: "156px",
                            },
                          },
                        },
                        list: {
                          "aria-labelledby": "long-button",
                          dense: true,
                        },
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {menuOptions.map((option: any, index: number) => (
                        <MenuItem
                          key={option.key || option.label || index}
                          dense
                          onClick={(e) => {
                            e.stopPropagation();
                            option.onClick(handleMenuClose, selectedRow);
                          }}
                          style={option.style}
                          sx={{
                            padding: "8px 16px !important",
                          }}
                        >
                          {option.icon &&
                            (React.isValidElement(option.icon) ? (
                              option.icon
                            ) : (
                              <img
                                src={option.icon}
                                alt={
                                  typeof option.label === "string"
                                    ? option.label
                                    : "icon"
                                }
                              />
                            ))}
                          {option.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </td>
            )}
        </>}
        </tr>
      </React.Fragment>
    );
  });

  return (
    <>
      <div className="mui-table">
        <OverlayScrollbarsComponent
          className="table-scroll-wrapper"
          options={{
            overflow: { x: "scroll", y: "scroll" },
            scrollbars: {
              theme: "os-theme-dark",
              autoHide: "scroll",
              autoHideDelay: 400,
            },
          }}
        >
          <table>
            <thead>
              <tr>
                {columnNames?.filter((header)=>header.key !== "milestone").map((header) => {
                  const isSortable = SORTABLE_COLUMNS.has(header.key);

                  return (
                    <th key={header.key}>
                      <div
                        className="header-column"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          width: "100%",
                        }}
                      >
                        <span>{header.label}</span>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {header?.filterable &&
                            (filters[header.key]?.length ?? 0) > 0 && (
                              <button
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    (filters[header.key]?.length ?? 0) === 0
                                  ) {
                                    return;
                                  }
                                  setOpenFilter(
                                    openFilter === header.key
                                      ? null
                                      : header.key,
                                  );
                                }}
                              >
                                <span
                                  ref={(el) => {
                                    filterIconRefs.current[header.key] = el;
                                  }}
                                  className="filter-pill"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  aria-label={`Filter ${header.label}`}
                                >
                                  <img
                                    src={FilterIcon}
                                    alt="Filter"
                                    className="filter-pill-icon"
                                  />
                                  {selectedFilters[header.key]?.length > 0 ? (
                                    <span className="filter-count">
                                      {selectedFilters[header.key].length}
                                    </span>
                                  ) : null}
                                </span>
                                {openFilter &&
                                  filters[openFilter]?.length > 0 &&
                                  ReactDOM.createPortal(
                                    <ClickAwayListener onClickAway={() => setOpenFilter(null)}>
                                      <div
                                        ref={filterRef}
                                        className="filter-dropdown"
                                        style={{
                                          position: "fixed",
                                          top: filterIconRefs.current[openFilter]
                                            ? `${filterIconRefs.current[openFilter].getBoundingClientRect().bottom + 5}px`
                                            : "0px",
                                          left: filterIconRefs.current[openFilter]
                                            ? `${filterIconRefs.current[openFilter].getBoundingClientRect().left}px`
                                            : "0px",
                                          zIndex: 10001,
                                          background: "#6e6d92",
                                          borderRadius: "8px",
                                          padding: "5px",
                                          width: "max-content",
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        {filters[openFilter]?.map((opt, index) => {
                                          const isSelected = selectedFilters[openFilter]?.some(
                                            (f) => f.key === opt.key,
                                          );
                                          const isLastOption = index === filters[openFilter].length - 1;
                                          return (
                                            <button
                                              key={opt.key}
                                              style={{
                                                border: "none",
                                                background: "none",
                                                padding: 0,
                                                margin: 0,
                                                cursor: "pointer",
                                              }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFilterSelect(openFilter, opt);
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "#403c6f";
                                                e.currentTarget.style.borderRadius = "4px";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.background = isSelected
                                                  ? "#403c6f"
                                                  : "transparent";
                                                e.currentTarget.style.borderRadius = isSelected
                                                  ? "4px"
                                                  : "6px";
                                              }}
                                            >
                                            <div
                                              key={opt.key}
                                              className={`filter-option ${isSelected ? "selected" : ""}`}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "8px 12px",
                                                borderRadius: isSelected ? "4px" : "6px",
                                                marginBottom: isLastOption ? "0" : "4px",
                                                cursor: "pointer",
                                                fontSize: "14px",
                                                color: "#fff",
                                                fontWeight: 600,
                                                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                                                background: isSelected ? "#403c6f" : "transparent",
                                                transition: "background 0.2s ease",
                                              }}
                                            >
                                              <span>{opt.value}</span>
                                              <input
                                                type="checkbox"
                                                readOnly
                                                checked={isSelected}
                                                style={{
                                                  width: "16px",
                                                  height: "16px",
                                                  pointerEvents: "none",
                                                  accentColor: isSelected ? "#fff" : "#403c6f",
                                                }}
                                              />
                                            </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </ClickAwayListener>,
                                    document.body,
                                )}
                              </button>  
                            )}

                          {isSortable && (
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenSortMenu(e, header.key);
                              }}
                            >
                              <img
                                src={TableHeaderArrow}
                                alt="Sort"
                                style={{
                                  cursor: "pointer",
                                  width: "12px",
                                  height: "12px",
                                  opacity:
                                    sortConfig?.key === header.key ? 1 : 0.4,
                                  transition: "opacity 0.2s ease",
                                }}
                                title="Sort options"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </th>
                  );
                })}

                {showChatColumn && <th></th>}
                {showInfoColumn && <th></th>}
                {showActionsColumn && <th></th>}
                {showActionsColumnWithOptions && <th></th>}
              </tr>
            </thead>

            <tbody>
              {sortedRows.length > 0 ? (
                viewSortedRows
              ) : (
                <tr>
                  <td
                    colSpan={
                      columnNames.length +
                      (showChatColumn ? 1 : 0) +
                      (showInfoColumn ? 1 : 0) +
                      (showActionsColumn ? 1 : 0) +
                      (showActionsColumnWithOptions ? 1 : 0)
                    }
                    style={{ background: "transparent" }}
                  >
                    <div className="no-results-popup">
                      <NoRecordsComponent
                        title={noRecordsTitle}
                        message={noRecordsMessage}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </OverlayScrollbarsComponent>

        {/* ✅ Render Popover outside scroll container but inside component */}
      </div>

      {Boolean(sortMenuAnchor) &&
        ReactDOM.createPortal(
          <Popover
            open={Boolean(sortMenuAnchor)}
            anchorEl={sortMenuAnchor}
            onClose={handleCloseSortMenu}
            container={document.body}
            disablePortal={false}
            disableScrollLock={true}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            disableRestoreFocus={true}
            hideBackdrop={false}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
            }}
            sx={{
              zIndex: 9999,
            }}
            slotProps={{
              paper: {
                sx: {
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
                  zIndex: 9999,
                },
                onClick: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  e.nativeEvent.stopImmediatePropagation();
                },
              },
              backdrop: {
                sx: {
                  backgroundColor: "transparent",
                },
              },
            }}
          >
            <button
              style={{
                padding:"0px",
                margin:"0px",
                cursor:"pointer",
                background:"none",
                border:"none",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
              }}
            >
              <div
                style={{
                  padding: "4px 0 8px",
                  minWidth: "150px",
                }}
              >
                {/* Ascending */}
                <button
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSortSelection("asc", e);
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) => {
                    if (
                      !(
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "asc"
                      )
                    ) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >

                <div
                  style={{
                    marginBottom: "5px",
                    padding: "8px 16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor:
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "asc"
                        ? "#f0f0f0"
                        : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    checked={
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "asc"
                    }
                    readOnly
                    style={{ cursor: "pointer", pointerEvents: "none" }}
                  />
                  <span style={{ fontSize: "13px" }}>Ascending</span>
                </div>
                </button>
                {/* Descending */}
                <button
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) => {
                    if (
                      !(
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "desc"
                      )
                    ) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSortSelection("desc", e);
                  }}
                >
                  <div
                    style={{
                      padding: "8px 16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor:
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "desc"
                          ? "#f0f0f0"
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      checked={
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "desc"
                      }
                      readOnly
                      style={{ cursor: "pointer", pointerEvents: "none" }}
                    />
                    <span style={{ fontSize: "13px" }}>Descending</span>
                  </div>
                </button>

                {/* Clear Sort */}
                {sortConfig?.key === sortMenuColumn && (
                  <>
                    <div
                      style={{
                        height: "1px",
                        backgroundColor: "#e0e0e0",
                        margin: "2px 0",
                      }}
                    />
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleSortSelection(null, e);
                      }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px 16px 0px",
                        }}
                      >
                        <span
                          style={{
                            color: "#d32f2f",
                            fontSize: "13px",
                          }}
                        >
                          Clear Sort
                        </span>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </button>
          </Popover>,
          document.body,
        )}

      {/* ✅ Filter dropdown rendered via Portal to document.body */}

    </>
  );
};

export default MUITable;
